import React, { useContext, useState, useRef, Fragment } from 'react';
import VisuallyHidden from '@reach/visually-hidden';
import { Context } from '@front10/helpers/dist/Context';
import dayjs from 'dayjs';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';
/**
 * Atoms
 */
import { FilterButton } from '../../lab/FilterButton';
/**
 * utils
 */
import { isNotNil } from '../../../helpers/utils';
import { formatDate } from '../DatePickerWidget/atoms/helper';

const DateSelectorFilter = (props, ref) => {
  const {
    ItemComponentInstance,
    dateRangeSettings,
    dateRangeSettings: {
      format,
      formatAriaLabel = 'MMM dddd DD, YYYY',
      disabled,
      flexibleDatesRangeValues,
      showDateSelectorButtonTitle
    },
    dateRange,
    dateRange: {
      onChange,
      value,
      value: { checkedFlexibleDates, flexibleDates }
    }
  } = props;
  const context = useContext(Context);
  const {
    translate,
    direction,
    theme: { designTokens }
  } = context;

  /**
   * componentType can be `SinleDatePicker` or `SingleDateRangePicker`
   */
  const componentType = ItemComponentInstance.render.componentName;
  const label = translate(
    `cmp-date-${componentType === 'SingleDateRangePicker' ? 'range-' : ''}selector-title`,
    'Departure Date'
  );
  const labelDeparture = translate(`cmp-date-range-departure-label`, 'Departure');
  const labelReturn = translate(`cmp-date-range-return-label`, 'Return');
  let buttonValue;
  let active;
  let defaultValue;
  let buttonAriaLabel;
  const [isOpen, setIsOpen] = useState(false);
  const filterButtonRef = useRef(null);
  const dateSelectorFilterRef = useRef(null);
  const announceAccessibilityMessage = useRef(false);
  const [enabledAccessibilityText, setEnabledAccessibilityText] = useState(false);
  const removeA11YMessage = () => {
    setTimeout(() => {
      setEnabledAccessibilityText(false);
    }, 150);
  };

  switch (componentType) {
    case 'SingleDatePicker': {
      buttonValue = isNotNil(value.start)
        ? `${
            showDateSelectorButtonTitle
              ? translate('cmp-date-selector-button-title', 'Departure')
              : ''
          } ${formatDate(dayjs(value.start).toDate(), format, context)}`
        : label;
      buttonAriaLabel = isNotNil(value.start)
        ? formatDate(dayjs(value.start).toDate(), formatAriaLabel, context)
        : '';
      active = isNotNil(value.start);
      defaultValue = value.start !== undefined ? dayjs(value.start).toDate() : undefined;
      break;
    }
    default: {
      buttonValue =
        !isNotNil(value.start) && !isNotNil(value.end)
          ? label
          : value.end
          ? `${formatDate(dayjs(value.start).toDate(), format, context)} - ${formatDate(
              dayjs(value.end).toDate(),
              format,
              context
            )}`
          : `${formatDate(dayjs(value.start).toDate(), format, context)}`;
      buttonAriaLabel =
        isNotNil(value.start) && isNotNil(value.end)
          ? [
              formatDate(dayjs(value.start).toDate(), formatAriaLabel, context),
              formatDate(dayjs(value.end).toDate(), formatAriaLabel, context)
            ]
          : isNotNil(value.start)
          ? formatDate(dayjs(value.start).toDate(), formatAriaLabel, context)
          : '';
      active = isNotNil(value.start) || isNotNil(value.end);
      defaultValue = value;
      break;
    }
  }

  if (checkedFlexibleDates) {
    const flexibleName = flexibleDatesRangeValues.values.filter(
      (i) => i.value === parseInt(flexibleDates)
    );
    buttonValue = `${buttonValue} ${
      flexibleName.length === 1 ? `(${translate(flexibleName[0].name)})` : ''
    } `;
  }

  React.useImperativeHandle(
    ref,
    () => ({
      focusToggleElement: () => {
        if (dateRangeSettings.enabledExperimentalPopover) {
          dateSelectorFilterRef?.current?.focusToggleElement();
        } else {
          filterButtonRef?.current?.focus();
        }
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  let newButtonAriaLabel = showDateSelectorButtonTitle
    ? `${translate('cmp-date-selector-button-title', 'Departure')} ${buttonAriaLabel}`
    : `${label} ${buttonAriaLabel}`;

  if (componentType === 'SingleDateRangePicker') {
    newButtonAriaLabel = showDateSelectorButtonTitle
      ? isNotNil(value.start) && isNotNil(value.end)
        ? `${labelDeparture}: ${buttonAriaLabel[0]} - ${labelReturn}: ${buttonAriaLabel[1]}`
        : isNotNil(value.start)
        ? `${labelDeparture}: ${buttonAriaLabel}`
        : label
      : `${label}`;
  }

  if (checkedFlexibleDates) {
    const flexibleName = flexibleDatesRangeValues.values.filter(
      (i) => i.value === parseInt(flexibleDates)
    );
    newButtonAriaLabel = `${newButtonAriaLabel} ${
      flexibleName.length === 1 ? `(${translate(flexibleName[0].name)})` : ''
    } `;
  }

  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>
        <Fragment>
          <ItemComponentInstance
            {...dateRangeSettings}
            {...dateRange}
            ref={componentType === 'SingleDateRangePicker' ? dateRange.ref : dateSelectorFilterRef}
            toggleElement={
              <FilterButton
                iconSettings={dateRangeSettings?.filterButtonIconProps}
                ref={filterButtonRef}
                value={buttonValue}
                aria-expanded={isOpen}
                aria-label={newButtonAriaLabel}
                aria-haspopup="dialog"
                active={active}
                disabled={disabled} // used to disabled the component
              />
            }
            onChange={(date) => {
              onChange({
                start: date.start,
                end: date.end,
                type: 'departure', // Is necessary this props ?
                flexibleDates: date.flexibleDates,
                availableDays: date.availableDays,
                checkedFlexibleDates: date.checkedFlexibleDates
              });
            }}
            defaultValue={defaultValue}
            changePopoverState={(isOpenValue) => {
              setIsOpen(isOpenValue);
              if (!isOpenValue) {
                removeA11YMessage();
                setTimeout(() => {
                  filterButtonRef?.current?.focus();
                }, 150);
                if (!dateRangeSettings.isMobile) {
                  announceAccessibilityMessage.current = true;
                }
              } else {
                setEnabledAccessibilityText(true);
                announceAccessibilityMessage.current = false;
              }
            }}
            checkedFlexibleDates={
              dateRange.defaultValue.checkedFlexibleDates !== undefined
                ? dateRange.defaultValue.checkedFlexibleDates
                : props.dateRangeSettings.checkedFlexibleDates
            }
            flexibleDates={flexibleDates}
          />
          <VisuallyHidden role="status">
            {isOpen && enabledAccessibilityText
              ? translate('cmp-departure-filte-expanded', 'Departure filter expanded')
              : ''}
          </VisuallyHidden>
          <VisuallyHidden role="status">
            {!isOpen && enabledAccessibilityText
              ? translate('cmp-departure-filte-collapsed', 'Departure filter collapsed')
              : ''}
          </VisuallyHidden>
          {
            <VisuallyHidden role="status">
              {announceAccessibilityMessage.current &&
              enabledAccessibilityText &&
              (dateRangeSettings.enabledExperimentalPopover === undefined ||
                dateRangeSettings.enabledExperimentalPopover === false)
                ? `${newButtonAriaLabel}`
                : ''}
            </VisuallyHidden>
          }
        </Fragment>
      </ThemeProvider>
    </EmotionCache>
  );
};

export default React.forwardRef(DateSelectorFilter);
